import { defaultTheme } from 'react-admin';
import { createTheme, Theme } from '@mui/material/styles';
import { Branding } from './services/brandingService';
import { tokens } from './designTokens';

/**
 * Theme-Aufbau aus der einzigen Token-Quelle (designTokens.ts). Früher waren die
 * Werte hier UND in azubi-web-styles.css hartkodiert und per Hand synchron
 * gehalten — jetzt kommt alles aus den Tokens.
 *
 * white-label: primary/secondary kommen pro Mandant aus dem Branding und
 * überschreiben die Token-Defaults; der Rest ist systemweit fix.
 */
const buildTheme = (primaryColor: string, secondaryColor: string, withShades: boolean): Theme => {
    const t = tokens;
    return createTheme({
        ...defaultTheme,
        palette: {
            primary: {
                main: primaryColor,
                ...(withShades ? { light: t.color.primaryLight, dark: t.color.primaryDark } : {}),
                contrastText: '#ffffff',
            },
            secondary: {
                main: secondaryColor,
                ...(withShades ? { light: t.color.secondaryLight, dark: t.color.secondaryDark } : {}),
                contrastText: '#ffffff',
            },
            error: { main: t.color.error, dark: t.color.errorDark },
            warning: { main: t.warn.main, dark: t.warn.dark, contrastText: t.warn.contrastText },
            info: { main: withShades ? primaryColor : primaryColor },
            success: { main: secondaryColor },
            text: {
                primary: t.color.text.primary,
                secondary: t.color.text.secondary,
            },
            background: {
                default: t.color.background.default,
                paper: t.color.background.paper,
            },
            divider: t.color.divider,
        },
        shape: { borderRadius: t.shape.radiusSm },
        typography: {
            fontFamily: t.typography.fontFamily,
            h1: { ...t.typography.h1 },
            h2: { ...t.typography.h2 },
            h3: { ...t.typography.h3 },
            h4: { ...t.typography.h4 },
            h5: { ...t.typography.h5 },
            h6: { ...t.typography.h6 },
            body1: { ...t.typography.body1 },
            button: { fontWeight: t.typography.button.fontWeight, textTransform: t.typography.button.textTransform },
        },
        components: {
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        background: t.color.background.default,
                        color: t.color.text.primary,
                        boxShadow: t.elevation.appBar,
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: { borderRadius: t.shape.radiusSm, padding: '8px 16px', boxShadow: 'none' },
                },
                variants: [
                    { props: { variant: 'contained', color: 'error' }, style: { backgroundColor: t.color.errorDark, color: '#ffffff', '&:hover': { backgroundColor: t.color.errorDarkHover } } },
                    { props: { variant: 'text', color: 'error' }, style: { color: t.color.errorDark } },
                    { props: { variant: 'outlined', color: 'error' }, style: { color: t.color.errorDark, borderColor: t.color.errorDark } },
                    { props: { variant: 'contained', color: 'warning' }, style: { backgroundColor: t.warn.dark, '&:hover': { backgroundColor: t.warn.darkHover } } },
                    { props: { variant: 'text', color: 'warning' }, style: { color: t.warn.dark } },
                    { props: { variant: 'outlined', color: 'warning' }, style: { color: t.warn.dark, borderColor: t.warn.dark } },
                ],
            },
            MuiDrawer: {
                styleOverrides: {
                    paper: { padding: 0 },
                },
            },
            MuiChip: {
                variants: [
                    { props: { variant: 'filled', color: 'warning' }, style: { backgroundColor: t.warn.solidBg, color: t.warn.solidText } },
                    { props: { variant: 'outlined', color: 'warning' }, style: { backgroundColor: t.warn.surfaceBg, color: t.warn.surfaceText, borderColor: t.warn.surfaceBorder } },
                ],
            },
            MuiAlert: {
                styleOverrides: {
                    standardWarning: {
                        backgroundColor: t.warn.surfaceBg,
                        color: t.warn.surfaceText,
                        '& .MuiAlert-icon': { color: t.warn.main },
                    },
                },
            },
        },
    });
};

/**
 * Create a dynamic theme based on branding colors.
 */
export const createDynamicTheme = (branding?: Branding | null): Theme => {
    const primaryColor = branding?.primaryColor || tokens.color.primary;
    const secondaryColor = branding?.secondaryColor || tokens.color.secondary;
    return buildTheme(primaryColor, secondaryColor, false);
};

// Default theme (used as fallback) — mit hellen/dunklen Markenabstufungen.
export const theme = buildTheme(tokens.color.primary, tokens.color.secondary, true);
